import { expressionLimits } from './scoring/expression';
import type { Challenge } from './definition';
import { playerConstruction } from './counting';

export interface AccountingTile {
  id?: string;
  tileType: string;
  meta: { partOfChip?: string | null };
  globalTileLocation: { location: { x: number; y: number } };
}
export interface AccountingChip {
  id: string;
  logicCircuitDto: { tileManagerDto: { tiles: readonly AccountingTile[] } };
  interfaceLinks?: readonly {
    logicTileId?: string;
    structureTileId?: string;
    inputMode?: string;
  }[];
  structureCircuitDto?: { tileManagerDto: { tiles: readonly AccountingTile[] } };
  chipInstanceDtos: readonly {
    id: string;
    chipId: string;
    originPointDto?: { x: number; y: number };
    rotation?: number;
  }[];
}
export interface ChipUsage {
  totalTiles: number;
  area: number;
  counts: Record<string, number>;
  chipCounts: Record<string, number>;
}
const MAX_TILES = 4096;
const MAX_WORK = 16_384;
const MAX_AREA = expressionLimits.value;

/** Version 3 BOM. Author IDs come exclusively from the trusted challenge, never
 * candidate metadata. Callers must also check authored definition integrity.
 * Physical graph safety is checked even behind opaque/fixed accounting boundaries.
 */
export function playerChipUsage(
  project: { mainChipId: string; chips: readonly AccountingChip[] },
  challenge: Challenge,
  includeRootStructure = false,
): ChipUsage {
  if (project.chips.length > 257) throw Error('Too many chip definitions.');
  const chips = new Map(project.chips.map((chip) => [chip.id, chip]));
  if (chips.size !== project.chips.length) throw Error('Duplicate chip definitions.');
  const root = chips.get(project.mainChipId);
  if (!root) throw Error('The challenge circuit is missing.');
  const authored = new Set(challenge.chipLibrary?.map(({ chip }) => chip.id));
  const active = new Set<string>();
  const physical = new Map<string, number>();
  const heights = new Map<string, number>();
  const expandedWork = new Map<string, number>();
  let work = 0;
  function check(id: string, depth: number): number {
    if (active.has(id)) throw Error('Recursive chip definitions are not allowed.');
    if (depth > 64) throw Error('Chip nesting exceeds 64 levels.');
    if (physical.has(id)) {
      if (depth + heights.get(id)! > 64) throw Error('Chip nesting exceeds 64 levels.');
      return physical.get(id)!;
    }
    const chip = chips.get(id);
    if (!chip) throw Error('A chip dependency is missing.');
    active.add(id);
    const tiles = chip.logicCircuitDto.tileManagerDto.tiles;
    work += tiles.length + chip.chipInstanceDtos.length + 1;
    if (work > MAX_WORK) throw Error('Chip accounting exceeds the safety budget.');
    const instances = new Set(chip.chipInstanceDtos.map((i) => i.id));
    if (instances.size !== chip.chipInstanceDtos.length)
      throw Error('Duplicate chip instance IDs.');
    for (const tile of tiles) {
      const { x, y } = tile.globalTileLocation.location;
      if (![x, y].every((v) => Number.isSafeInteger(v) && Math.abs(v) <= 16000))
        throw Error('Invalid tile position.');
      if (tile.meta.partOfChip != null && !instances.has(tile.meta.partOfChip))
        throw Error('A chip tile has no matching instance.');
    }
    let size = tiles.length;
    let height = 0;
    // Empty definitions still allocate/traverse an instance during hydration.
    // Bound the expanded DAG, not just unique definitions or nonempty tiles.
    let expansion = tiles.length + 1;
    for (const i of chip.chipInstanceDtos) {
      size += check(i.chipId, depth + 1);
      height = Math.max(height, 1 + heights.get(i.chipId)!);
      expansion += expandedWork.get(i.chipId)!;
      if (expansion > MAX_WORK)
        throw Error('Construction exceeds the expanded chip work limit.');
    }
    heights.set(id, height);
    expandedWork.set(id, expansion);
    if (size > MAX_TILES) throw Error('Construction exceeds 4,096 expanded tiles.');
    active.delete(id);
    physical.set(id, size);
    return size;
  }
  // Unused definitions cost zero but must still be safe to hydrate/edit.
  for (const chip of project.chips) check(chip.id, 0);
  const cache = new Map<string, ChipUsage>();
  function count(chip: AccountingChip, isRoot = false): ChipUsage {
    if (!isRoot && cache.has(chip.id)) return cache.get(chip.id)!;
    const all = chip.logicCircuitDto.tileManagerDto.tiles;
    const { tiles, instances } = isRoot
      ? playerConstruction(all, chip.chipInstanceDtos, challenge)
      : { tiles: all, instances: chip.chipInstanceDtos };
    const byInstance = new Map(instances.map((i) => [i.id, i]));
    const result: ChipUsage = { totalTiles: 0, area: 0, counts: {}, chipCounts: {} };
    let left = Infinity,
      right = -Infinity,
      top = Infinity,
      bottom = -Infinity;
    const cells = new Set<string>();
    const structureById = new Map(
      (chip.structureCircuitDto?.tileManagerDto.tiles ?? [])
        .filter((tile) => tile.id != null)
        .map((tile) => [tile.id!, tile]),
    );
    const linksByLogic = new Map(
      (chip.interfaceLinks ?? [])
        .filter((link) => link.logicTileId != null)
        .map((link) => [link.logicTileId!, link]),
    );
    const hiddenControl = (tile: AccountingTile) => {
      if ((isRoot && !includeRootStructure) || tile.meta.partOfChip != null) return false;
      const link = tile.id ? linksByLogic.get(tile.id) : undefined;
      const control =
        tile.tileType === 'Lever' ||
        tile.tileType === 'Button' ||
        (tile.tileType === 'SingleInterface' &&
          (link?.inputMode === 'lever' || link?.inputMode === 'button'));
      if (!control) return false;
      const projection = link?.structureTileId
        ? structureById.get(link.structureTileId)
        : undefined;
      return !projection || projection.tileType !== tile.tileType;
    };

    // Interface/gate overlays take precedence over decorative Structure cells.
    for (const tile of [...tiles].sort(
      (a, b) => Number(a.tileType === 'Structure') - Number(b.tileType === 'Structure'),
    )) {
      const { x, y } = tile.globalTileLocation.location;
      left = Math.min(left, x);
      right = Math.max(right, x);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
      const owner = tile.meta.partOfChip;
      if (owner != null && !authored.has(byInstance.get(owner)!.chipId)) continue;
      // A repeated projection of the same physical cell is not another tile.
      const key = `${owner ?? ''}:${x},${y}`;
      if (cells.has(key)) continue;
      cells.add(key);
      if (!hiddenControl(tile)) result.totalTiles++;
      result.counts[tile.tileType] = (result.counts[tile.tileType] ?? 0) + 1;
    }
    result.area = tiles.length ? (right - left + 1) * (bottom - top + 1) : 0;
    // Gray Structure cells are distinct material. Ports mirrored from Logic are
    // projections, so never count other Structure-tab kinds a second time.
    if (!isRoot || includeRootStructure) {
      const structureCells = new Set<string>();
      for (const tile of chip.structureCircuitDto?.tileManagerDto.tiles ?? []) {
        if (tile.tileType !== 'Structure') continue;
        const { x, y } = tile.globalTileLocation.location;
        const key = `${x},${y}`;
        if (structureCells.has(key)) continue;
        structureCells.add(key);
        result.totalTiles++;
        result.counts.Structure = (result.counts.Structure ?? 0) + 1;
      }
    }
    for (const instance of instances) {
      result.chipCounts[instance.chipId] = (result.chipCounts[instance.chipId] ?? 0) + 1;
      if (authored.has(instance.chipId)) continue;
      const child = count(chips.get(instance.chipId)!);
      for (const [id, n] of Object.entries(child.chipCounts))
        result.chipCounts[id] = (result.chipCounts[id] ?? 0) + n;
      result.totalTiles += child.totalTiles;
      result.area += child.area;
      for (const [type, n] of Object.entries(child.counts))
        result.counts[type] = (result.counts[type] ?? 0) + n;
    }
    if (!Number.isSafeInteger(result.area) || result.area > MAX_AREA)
      throw Error('Recursive area exceeds the supported range.');
    cache.set(chip.id, result);
    return result;
  }
  return count(root, true);
}

/** Read observable authored geometry without serializing simulation graphs. */
export function playerUsageFromContext(
  context: import('@/context').default,
  mainChipId: string = context.mainChip.id,
  preview = false,
) {
  const challenge = context.challenge!;
  return playerChipUsage(
    {
      mainChipId,
      chips: context.chipStore.chips.map((chip) => ({
        id: chip.id,
        interfaceLinks: chip.interfaceLinks.map((link) => link.dto),
        logicCircuitDto: {
          tileManagerDto: {
            tiles: chip.logicCircuit.circuitModel.tiles.map((t) => ({
              id: t.id,
              tileType: t.tileType,
              meta: { partOfChip: t.meta.partOfChip },
              globalTileLocation: { location: { x: t.location.x, y: t.location.y } },
            })),
          },
        },
        structureCircuitDto: {
          tileManagerDto: {
            tiles: chip.structureCircuit.circuitModel.tiles.map((t) => ({
              id: t.id,
              tileType: t.tileType,
              meta: { partOfChip: t.meta.partOfChip },
              globalTileLocation: { location: { x: t.location.x, y: t.location.y } },
            })),
          },
        },
        chipInstanceDtos: chip.logicCircuit.circuitModel.chips.map((i) => ({
          id: i.id,
          chipId: i.chipId,
          originPointDto: { x: i.originPoint.x, y: i.originPoint.y },
          rotation: i.rotation,
        })),
      })),
    },
    preview
      ? { ...challenge, inputs: [], outputs: [], fixedTiles: [], starterChips: [] }
      : challenge,
    preview,
  );
}

/** Only root placements are grouped; nested costs are included in each copy. */
export function rootChipBudgetBreakdown(
  context: import('@/context').default,
  type: string,
  resource: 'tiles' | 'chips' = 'tiles',
) {
  const challenge = context.challenge!;
  const field = resource === 'tiles' ? 'counts' : 'chipCounts';
  const total = playerUsageFromContext(context)[field][type] ?? 0;
  const root = context.mainChip.logicCircuit.circuitModel;
  const { instances } = playerConstruction(
    [],
    root.chips.map((i) => ({
      id: i.id,
      chipId: i.chipId,
      originPointDto: { x: i.originPoint.x, y: i.originPoint.y },
      rotation: i.rotation,
    })),
    challenge,
  );
  const grouped = new Map<string, number>();
  for (const i of instances) {
    if (challenge.chipLibrary?.some((rule) => rule.chip.id === i.chipId)) continue;
    grouped.set(i.chipId, (grouped.get(i.chipId) ?? 0) + 1);
  }
  const chips = [...grouped]
    .map(([id, quantity]) => {
      const each = playerUsageFromContext(context, id, true)[field][type] ?? 0;
      return {
        id,
        name: context.chipStore.chips.find((chip) => chip.id === id)!.name,
        quantity,
        each,
        total: each * quantity,
      };
    })
    .filter((group) => group.total > 0);
  return {
    total,
    board: total - chips.reduce((sum, group) => sum + group.total, 0),
    chips,
  };
}
